import { useCallback, useEffect, useState } from 'react';
import {
  useBlocker,
} from 'react-router-dom';

import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import { useConfirmModal } from '@shared/components/modal/confirm-modal/ConfirmModal';

type FormPromptProps = {
  onSave: () => void;
  onDiscard?: () => void;
  isFormDirty: boolean;
  title?: any;
  body?: any;
};

const i18n = LocaleService.getTranslations('general');

const useFormPrompt = ({
  onSave,
  onDiscard,
  isFormDirty = false,
  title = i18n.formPrompt.title,
  body = i18n.formPrompt.body,
}: FormPromptProps) => {
  const blocker = useBlocker(isFormDirty);
  const [ confirming, isConfirming ] = useState(false);
  const { show } = useConfirmModal();
  const confirm = useCallback(async () => {
    if (!isFormDirty) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      show({
        title,
        body,
        confirmBtn: {
          name: i18n.formPrompt.button.confirm.label,
        },
        cancelBtn: {
          name: i18n.formPrompt.button.cancel.label,
        },
        onConfirm: async () => {
          try {
            await onSave();
            resolve(true);
          } catch (error) {
            handleError(error);
            resolve(false);
          }
        },
        onCancel: () => {
          onDiscard?.();
          resolve(true);
        },
      });
    });
  }, [
    isFormDirty,
    onSave,
    onDiscard,
    show,
    title,
    body,
  ]);

  useEffect(() => {
    if (blocker.state === 'blocked' && !confirming) {
      isConfirming(true);
      const proceedIfConfirmed = async () => {
        try {
          const result = await confirm();
          if (result && blocker.state === 'blocked') {
            blocker.proceed();
          }
          // Reset blocker if canceled
          blocker.reset();
        } catch (error) {
          // Ensure blocker is reset if an error occurs
          blocker.reset();
        } finally {
          isConfirming(false);
        }
      };

      proceedIfConfirmed();
    }
  }, [ blocker, confirm, isFormDirty, confirming ]);

  useEffect(() => {
    if (isFormDirty) {
      window.onbeforeunload = () => body;
    }
    return () => {
      window.onbeforeunload = null;
    };
  }, [ isFormDirty, body ]);

  return {
    confirm,
  };
};

export default useFormPrompt;
